import { io, Socket } from 'socket.io-client';
import { AppState } from 'react-native';
import { useStrokeStore, registerOnStrokeEndCallback, unregisterOnStrokeEndCallback } from '../store/useStrokeStore';
import { useNotificationStore } from '../store/NotificationStore';
import { useAuthStore } from '../store/useAuthStore';
import { useActivityStore } from '../store/useActivityStore';
import { useAccommodationStore } from '../store/useAccommodationStore';
import { OfflineFirstStorage } from '../repositories/OfflineFirstStorage';
import { TaskCompletionService } from './TaskCompletionService';
import { AppConfig } from '../config/AppConfig';

let SYNC_SERVER_URL = AppConfig.DEFAULT_SERVER_URL;

/**
 * Service to handle real-time synchronization of strokes to the teacher portal.
 * Uses Socket.io for proper WebSocket communication.
 */
export class StrokeSyncService {
    private static socket: Socket | null = null;
    private static interval: ReturnType<typeof setInterval> | null = null;
    private static appStateSubscription: { remove(): void } | null = null;

    /**
     * WP-112: disconnect the socket while the app is backgrounded (covers RN
     * native AND the react-native-web PWA, where AppState maps to document
     * visibility) — an open socket pins the Azure replica and defeats
     * scale-to-zero; a parked device's reconnect loop produces constant
     * wake-up traffic.
     */
    private static ensureAppStateListener() {
        if (this.appStateSubscription) return;
        this.appStateSubscription = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
                if (this.socket && !this.socket.connected) {
                    console.log('[Sync] App foregrounded — reconnecting socket');
                    this.socket.connect();
                }
            } else if (this.socket) {
                // background / inactive — release the server replica and stop
                // any in-progress reconnect attempts (disconnect() halts them)
                console.log('[Sync] App backgrounded — disconnecting socket');
                this.socket.disconnect();
            }
        });
    }

    static setServerUrl(url: string) {
        SYNC_SERVER_URL = url;
        console.log(`[Sync] Server URL updated to: ${SYNC_SERVER_URL}`);
    }

    static getServerUrl(): string {
        return SYNC_SERVER_URL;
    }

    private static async tryPing(url: string, timeout: number): Promise<boolean> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);
            const res = await fetch(`${url}/health`, {
                method: 'GET',
                headers: { 'User-Agent': 'EduWriteStudent/1.0' },
                signal: controller.signal as AbortSignal
            });
            clearTimeout(timeoutId);
            return res.ok;
        } catch {
            return false;
        }
    }

    static async discoverAndSetServerUrl(cloudFallbackUrl: string): Promise<string> {
        const primaryUrl = cloudFallbackUrl || AppConfig.DEFAULT_SERVER_URL;

        // --- Attempt 1: Primary URL ---
        console.log(`[Sync] Attempting primary URL: ${primaryUrl}`);
        if (await this.tryPing(primaryUrl, AppConfig.TIMEOUTS.PRIMARY_PING)) {
            console.log(`[Sync] Primary DNS connection successful: ${primaryUrl}`);
            this.setServerUrl(primaryUrl);
            return primaryUrl;
        }
        console.warn(`[Sync] Primary URL ${primaryUrl} failed — may be stale DNS cache`);

        // --- Attempt 2: Retry primary after short delay (DNS cache may flush) ---
        console.log(`[Sync] Retrying primary in ${AppConfig.TIMEOUTS.DNS_RETRY_DELAY}ms...`);
        await new Promise(r => setTimeout(r, AppConfig.TIMEOUTS.DNS_RETRY_DELAY));
        if (await this.tryPing(primaryUrl, AppConfig.TIMEOUTS.PRIMARY_PING)) {
            console.log(`[Sync] Primary retry successful: ${primaryUrl}`);
            this.setServerUrl(primaryUrl);
            return primaryUrl;
        }
        console.warn(`[Sync] Primary retry also failed`);

        // --- Attempt 3: DNS fallback hosts (CNAME target, direct IP) ---
        for (const url of AppConfig.DNS_FALLBACK_HOSTS) {
            console.log(`[Sync] Trying DNS fallback: ${url}`);
            if (await this.tryPing(url, AppConfig.TIMEOUTS.PRIMARY_PING)) {
                console.log(`[Sync] DNS fallback successful: ${url}`);
                this.setServerUrl(url);
                return url;
            }
        }

        // --- Attempt 4: Local network candidates ---
        for (const url of AppConfig.DISCOVERY_CANDIDATES) {
            console.log(`[Sync] Trying local candidate: ${url}`);
            if (await this.tryPing(url, AppConfig.TIMEOUTS.CANDIDATE_PING)) {
                console.log(`[Sync] Local candidate successful: ${url}`);
                this.setServerUrl(url);
                return url;
            }
        }

        throw new Error(`Server unreachable at ${primaryUrl} and no fallbacks responded`);
    }

    static startSync() {
        this.ensureAppStateListener();

        if (this.socket?.connected) {
            // Re-announce join because classId might have changed
            const { studentName, deviceId, classId, studentId, groupId, cohortIds } = useAuthStore.getState();
            if (classId && studentName) {
                this.socket?.emit('student:join', {
                    classId,
                    studentName,
                    deviceId,
                    studentId,
                    groupId
                });
                console.log(`[Sync] Re-announced join for class: ${classId} (studentId: ${studentId}, groupId: ${groupId})`);
            }
            // WP-075: Re-join cohort rooms
            if (cohortIds && cohortIds.length > 0 && studentId) {
                for (const cohortId of cohortIds) {
                    this.socket?.emit('student:join_cohort', { cohortId, studentId });
                }
            }
            if (studentId) {
                void useAccommodationStore.getState().fetchAccommodations(
                    studentId,
                    SYNC_SERVER_URL,
                    useAuthStore.getState().deviceToken || undefined
                );
            }
            return;
        }

        if (this.socket) {
            // Socket exists but disconnected (e.g. paused in background) —
            // reuse it so its registered handlers survive.
            this.socket.connect();
            return;
        }

        // Register the stroke-end callback so useStrokeStore can notify us
        // without importing this file (which would create a circular dependency).
        registerOnStrokeEndCallback(() => StrokeSyncService.sendFinalStroke());

        console.log('[Sync] Connecting to sync server...');

        // Connect to sync server
        this.socket = io(SYNC_SERVER_URL, {
            auth: { token: useAuthStore.getState().deviceToken },
            transports: ['websocket', 'polling'],
            // WP-112: cap reconnect cadence — default ~1-5s infinite retries
            // produce constant wake-up traffic against a scaled-to-zero server.
            reconnectionDelayMax: 30000
        });

        this.socket.on('connect', () => {
            console.log('[Sync] Connected to sync server');

            // Announce student joining
            const { studentName, deviceId, classId, studentId, groupId, cohortIds } = useAuthStore.getState();
            if (classId && studentName) {
                this.socket?.emit('student:join', {
                    classId,
                    studentName,
                    deviceId,
                    studentId,
                    groupId
                });
            }

            // WP-075: Join cohort rooms in addition to class room
            if (cohortIds && cohortIds.length > 0 && studentId) {
                for (const cohortId of cohortIds) {
                    this.socket?.emit('student:join_cohort', {
                        cohortId,
                        studentId,
                    });
                }
                console.log(`[Sync] Joined ${cohortIds.length} cohort room(s)`);
            }

            if (studentId) {
                void useAccommodationStore.getState().fetchAccommodations(
                    studentId,
                    SYNC_SERVER_URL,
                    useAuthStore.getState().deviceToken || undefined
                );
            }

            // Flush offline submissions
            this.flushOfflineSubmissions();
        });

        // WP-106: Listen for silent student accommodations hydration
        this.socket.on('student:accommodations', (profile: any) => {
            console.log('[Sync] Received silent student accommodations');
            useAccommodationStore.getState().setProfile(profile);
        });

        this.socket.on('disconnect', () => {
            console.log('[Sync] Disconnected from sync server');
        });

        // Listen for teacher forced session termination
        this.socket.on('session:terminated', (data: { reason: string }) => {
            console.warn('[Sync] Session terminated by server:', data.reason);
            // Alert user and leave class
            useStrokeStore.getState().setToast({
                id: Date.now().toString(),
                feedback: `Session ended: ${data.reason}`,
                timestamp: Date.now(),
                read: false
            });
            // We use a small delay to let the toast be seen before state wipes
            setTimeout(() => {
                StrokeSyncService.stopSync();
                useAuthStore.getState().leaveClass();
                useActivityStore.getState().resetActivities();
                useStrokeStore.getState().clearStrokes();
            }, 3000);
        });

        // Listen for manual session reset from teacher
        this.socket.on('student:teacher_start_lesson', () => {
            console.log('[Sync] Teacher started a new lesson/reset session. Clearing local state.');
            useActivityStore.getState().resetActivities();
            useStrokeStore.getState().clearStrokes();
            useAuthStore.getState().setReady(false);
            useStrokeStore.getState().setToast({
                id: Date.now().toString(),
                feedback: "Teacher reset the session for a new lesson!",
                timestamp: Date.now(),
                read: false
            });
        });

        this.socket.on('connect_error', (error) => {
            console.error('[Sync] Connection error:', error.message);
        });

        // Listen for teacher feedback
        this.socket.on('student:suggestion', (data: { feedback: string; overlay?: any; targetText?: string; originalStrokes?: any[] }) => {
            // Log just count, not full array
            const strokeCount = (data.originalStrokes?.length || 0);
            console.log('[Sync] Received teacher suggestion:', data.feedback, 'with overlay:', data.overlay?.length || 0, 'original strokes:', strokeCount);

            // Add to notification history (persistent) using new store
            useNotificationStore.getState().addNotification({
                id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                title: 'Teacher Feedback',
                message: data.feedback,
                timestamp: Date.now(),
                read: false,
                type: 'feedback',
                payload: {
                    targetText: data.targetText,
                    overlay: data.overlay,
                    originalStrokes: data.originalStrokes
                }
            });

            // Check if we are on the relevant activity
            const { activities, currentActivityIndex } = useActivityStore.getState();
            const currentActivity = activities[currentActivityIndex];
            const currentTargetText = currentActivity?.config?.targetText || 'Unknown';

            // Auto-apply ONLY if activity matches
            if (!data.targetText || data.targetText === currentTargetText) {
                // Same activity: Show overlay immediately
                useStrokeStore.getState().setTeacherSuggestion(data);
                // Also show toast (optional, overlay might be enough, but toast gives message)
                // We'll let the overlay component handle the message display for now
            } else {
                // Different activity: Show Toast to alert student
                // We need to construct a full Notification object for the toast
                // We will use the legacy format for activeToast in StrokeStore for compability with TeacherFeedbackOverlay
                const toastNotification = {
                    id: Date.now().toString(),
                    feedback: data.feedback,
                    overlay: data.overlay,
                    targetText: data.targetText,
                    timestamp: Date.now(),
                    read: false
                };
                useStrokeStore.getState().setToast(toastNotification);
            }
        });

        // Listen for forced activity changes (full IActivity or legacy config-only)
        this.socket.on('student:next_activity', (data: { activity?: any; config?: any }) => {
            const received = data.activity ?? null;
            if (received) {
                console.log('[Sync] Teacher pushed full activity:', received.title);
                useActivityStore.getState().pushActivity(received);
            } else if (data.config) {
                // Legacy path: server sent only config
                console.log('[Sync] Teacher forced activity (legacy config):', data.config);
                useActivityStore.getState().pushActivityFromConfig(data.config);
            }

            // If the activity was queued (meaning student is busy), push a toast
            const { pendingActivities } = useActivityStore.getState();
            if (pendingActivities.length > 0) {
                useStrokeStore.getState().setToast({
                    id: Date.now().toString(),
                    feedback: "Teacher assigned a new task! Check your pending tasks.",
                    timestamp: Date.now(),
                    read: false
                });
            }
        });

        // Listen for list of activities (algorithmic batch generation)
        this.socket.on('student:next_activities_list', (data: { activities: any[] }) => {
            const received = data.activities ?? [];
            if (received.length > 0) {
                console.log(`[Sync] Teacher pushed ${received.length} dynamic activities.`);
                useActivityStore.getState().pushActivities(received);
            }

            // If tasks were queued, push a toast
            const { pendingActivities } = useActivityStore.getState();
            if (pendingActivities.length > 0) {
                useStrokeStore.getState().setToast({
                    id: Date.now().toString(),
                    feedback: "Teacher assigned a new task set! Check your pending tasks.",
                    timestamp: Date.now(),
                    read: false
                });
            }
        });

        // Poll and send stroke updates every 200ms
        this.interval = setInterval(() => {
            const { completedStrokes, currentStroke, structuredData } = useStrokeStore.getState();
            const { classId, deviceId, studentId } = useAuthStore.getState();

            if (classId && this.socket?.connected) {
                // Send all strokes (completed + current if exists)
                const allStrokes = currentStroke
                    ? [...completedStrokes, currentStroke]
                    : completedStrokes;

                // Send update if there are strokes OR structured data (like math answers)
                if (allStrokes.length > 0 || structuredData) {
                    const { activities, currentActivityIndex } = useActivityStore.getState();
                    const currentActivity = activities[currentActivityIndex];

                    this.socket.emit('student:strokes', {
                        classId,
                        deviceId,
                        studentId,
                        strokes: allStrokes,
                        targetText: currentActivity?.title || currentActivity?.config?.targetText || 'Unknown',
                        activityId: currentActivity?.id,
                        activityType: currentActivity?.type,
                        activityConfig: currentActivity?.config,
                        structuredData
                    });
                }
            }
        }, 200);
    }

    static stopSync() {
        // WP-112: release the AppState subscription
        this.appStateSubscription?.remove();
        this.appStateSubscription = null;

        // Stop polling
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }

        // Notify server and disconnect
        if (this.socket) {
            const { classId, deviceId, studentId } = useAuthStore.getState();
            if (classId) {
                this.socket.emit('student:leave', { classId, deviceId, studentId });
            }
            this.socket.disconnect();
            this.socket = null;
        }

        // Unregister stroke-end callback
        unregisterOnStrokeEndCallback();

        // WP-106: Reset accommodation profile on session stop
        useAccommodationStore.getState().reset();

        console.log('[Sync] Sync stopped');
    }

    static async sendFinalStroke() {
        const { completedStrokes, structuredData } = useStrokeStore.getState();
        const { classId, deviceId, studentId } = useAuthStore.getState();

        if (classId && this.socket?.connected && (completedStrokes.length > 0 || structuredData)) {
            const { activities, currentActivityIndex } = useActivityStore.getState();
            const currentActivity = activities[currentActivityIndex];

            this.socket.emit('student:strokes', {
                classId,
                deviceId,
                studentId,
                strokes: completedStrokes,
                targetText: currentActivity?.title || currentActivity?.config?.targetText || 'Unknown',
                activityId: currentActivity?.id,
                activityType: currentActivity?.type,
                activityConfig: currentActivity?.config,
                structuredData
            });
            console.log('[Sync] Update sent, total strokes:', completedStrokes.length, 'hasStructured:', !!structuredData);
        }
    }

    static sendReady(isReady: boolean) {
        const { classId, deviceId, studentId } = useAuthStore.getState();
        if (classId && this.socket?.connected) {
            this.socket.emit('student:ready', {
                classId,
                deviceId,
                studentId,
                isReady
            });
            console.log(`[Sync] Sent ready check: ${isReady}`);
        }
    }

    static sendQuizAnswer(data: {
        activityId: string;
        questionId: string;
        selectedOptionId: string;
        questionIndex: number;
        totalQuestions: number;
        score: number;
    }) {
        const { classId, deviceId, studentId } = useAuthStore.getState();
        if (classId && this.socket?.connected) {
            this.socket.emit('student:quiz_answer', {
                classId,
                deviceId,
                studentId,
                activityId: data.activityId,
                questionId: data.questionId,
                selectedOptionId: data.selectedOptionId,
                questionIndex: data.questionIndex,
                totalQuestions: data.totalQuestions,
                score: data.score,
            });
            console.log(`[Sync] Sent quiz answer: q${data.questionIndex + 1}/${data.totalQuestions}, score=${data.score}`);
        }
    }

    static requestHelp() {
        const { classId, deviceId, studentId, studentName } = useAuthStore.getState();
        if (classId && this.socket?.connected) {
            this.socket.emit('student:request_help', {
                classId,
                deviceId,
                studentId,
                studentName,
            });
            console.log(`[Sync] Sent silent help request for ${studentName || studentId}`);
        }
    }

    static async flushOfflineSubmissions() {
        if (!this.socket?.connected) return;

        const pending = await OfflineFirstStorage.getPendingSubmissions();
        if (pending.length > 0) {
            console.log(`[Sync] Found ${pending.length} pending submissions to sync...`);

            for (const submission of pending) {
                try {
                    // Emit to server (assuming server has a student:submit handler)
                    this.socket.emit('student:submit', submission);
                    console.log(`[Sync] Successfully synced cached submission: ${submission.id}`);
                } catch {
                    console.warn(`[Sync] Failed to sync submission ${submission.id}, will retry later`);
                }
            }

            await OfflineFirstStorage.clearPending();
        }

        // WP-Offline: Flush pending task completions
        const pendingCompletions = await OfflineFirstStorage.getPendingTaskCompletions();
        if (pendingCompletions.length > 0) {
            console.log(`[Sync] Found ${pendingCompletions.length} pending task completions to sync...`);
            await OfflineFirstStorage.clearPendingTaskCompletions();

            for (const comp of pendingCompletions) {
                try {
                    await TaskCompletionService.markTaskComplete(
                        comp.studentId,
                        comp.assignmentId,
                        comp.serverUrl,
                        comp.score
                    );
                    console.log(`[Sync] Successfully synced cached task completion: ${comp.assignmentId}`);
                } catch {
                    console.warn(`[Sync] Failed to sync task completion ${comp.assignmentId}, will retry later`);
                }
            }
        }
    }
}
